import { CoreCard, CoreChip, CoreExpandButton, CoreSearchField, CoreTabs } from "@verity/components/core";
import { DMCAQuestion, ContentProtectionState, isTakedownSectionExpanded } from ".";
import i18n from "../../../../i18n";
import TakedownIcon from "../../../../images/portal/takedown.svg?react";
import styles from "./TakedownSection.module.css";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { ReactNode, useId, useMemo, useState } from "react";
import { CompromisedTabpanel, FrequentQuestionsTabpanel, SafeQuestionTabpanel } from "./TakedownTabpanels";
import TakedownNoResultsImage from "../../../../images/portal/takedown-no-results.svg?react";
import { useMobileLayout } from "@verity/components";
import { AssessmentQuestion } from "@verity/api-client/types";
import { Trans } from "react-i18next";

type TakedownSectionProps = {
	pageState: ContentProtectionState;
	questions: DMCAQuestion[];
	lastFetchedDate: string | null;
};

const TAKEDOWN_TAB_KEYS = ["compromisedQuestions", "freqUsedQuestions", "safeQuestions"] as const;
type TakedownTab = (typeof TAKEDOWN_TAB_KEYS)[number];

type TakedownTabtype = {
	key: TakedownTab;
	label: ReactNode;
	id: string;
};

type TabQuestions = Record<TakedownTab, DMCAQuestion[]>;

export default function TakedownSection({ pageState, questions, lastFetchedDate }: TakedownSectionProps) {
	const [selectedTab, setSelectedTab] = useState<TakedownTab>("compromisedQuestions");
	const [searchText, setSearchText] = useState<string>("");

	const expanded = isTakedownSectionExpanded(pageState);
	const controlId = useId();
	const tabId = useId();
	const isMobileLayout = useMobileLayout();

	const tabpanelIdRecord: Record<TakedownTab, string> = Object.fromEntries(
		TAKEDOWN_TAB_KEYS.map((tabKey) => [tabKey, `${tabId}-${tabKey}`]),
	) as Record<TakedownTab, string>;

	const filteredTabQuestions = useMemo(() => {
		const filteredQuestions: TabQuestions = {
			compromisedQuestions: [],
			freqUsedQuestions: [],
			safeQuestions: [],
		};

		questions.forEach((question) => {
			const query = getQuestionText(question);
			if (!query || (searchText && !query.toLowerCase().includes(searchText.toLowerCase()))) return;

			if (question.numSources === 0) {
				filteredQuestions.safeQuestions.push(question);
			} else if (question.numSources <= 5) {
				filteredQuestions.compromisedQuestions.push(question);
			} else {
				filteredQuestions.freqUsedQuestions.push(question);
			}
		});

		return filteredQuestions;
	}, [questions, searchText]);

	const tabList = (tabQuestions: TabQuestions) =>
		TAKEDOWN_TAB_KEYS.map((tabKey) => ({
			key: tabKey,
			label: (
				<Typography component="span" fontWeight={tabKey === selectedTab ? 600 : 400} fontSize="15px">
					{i18n.t(`portal.components.contentProtectionPageTSX.review.${tabKey}.title`)}
					<CoreChip
						variant="rectangular"
						chipColor={tabKey === "compromisedQuestions" ? "#F8DCDB" : "#E6E6E6"}
						className={styles.tabChip}
						sx={{
							color: tabKey === "compromisedQuestions" ? "#B50003" : "#242424",
						}}
					>
						{tabQuestions[tabKey].length}
					</CoreChip>
				</Typography>
			),
			id: tabpanelIdRecord[tabKey],
		})) as TakedownTabtype[];
	return (
		<CoreCard
			title={i18n.t("portal.components.contentProtectionPageTSX.review.title")}
			description={
				<Typography className={styles.takedownDescription}>
					{expanded
						? i18n.t("portal.components.contentProtectionPageTSX.review.afterScanDescription")
						: i18n.t("portal.components.contentProtectionPageTSX.review.beforeScanDescription")}
				</Typography>
			}
			useHeader
			icon={<TakedownIcon fill="#42296E" />}
			className={expanded ? "" : styles.disabledSection}
		>
			{expanded && (
				<Stack spacing={1} paddingTop={"5px"}>
					<CoreSearchField
						showSearchButton={!isMobileLayout}
						ariaControls={controlId}
						totalResults={questions.length}
						onSearch={(value: string) => setSearchText(value)}
						label={i18n.t("portal.components.contentProtectionPageTSX.review.searchFieldPlaceholder")}
						sx={{
							flexGrow: isMobileLayout ? "1" : "0.7",
						}}
						value={searchText}
					/>
					<CoreTabs<TakedownTab>
						tabs={tabList(filteredTabQuestions)}
						value={selectedTab}
						onChange={(_, newTab) => setSelectedTab(newTab)}
					/>
					{selectedTab === "compromisedQuestions" && (
						<CompromisedTabpanel
							questions={filteredTabQuestions.compromisedQuestions}
							tabId={tabpanelIdRecord[selectedTab]}
							controlId={controlId}
							lastFetchedDate={lastFetchedDate}
						/>
					)}
					{selectedTab === "freqUsedQuestions" && (
						<FrequentQuestionsTabpanel
							questions={filteredTabQuestions.freqUsedQuestions}
							tabId={tabpanelIdRecord[selectedTab]}
							controlId={controlId}
						/>
					)}
					{selectedTab === "safeQuestions" && (
						<SafeQuestionTabpanel
							questions={filteredTabQuestions.safeQuestions}
							tabId={tabpanelIdRecord[selectedTab]}
							controlId={controlId}
						/>
					)}
				</Stack>
			)}
		</CoreCard>
	);
}

export function getQuestionText(question: AssessmentQuestion | DMCAQuestion): string | null {
	if (question.bodyText?.trim()) {
		return question.bodyText;
	}

	if (question.title?.trim()) {
		return question.title;
	}

	return null;
}

export function TakedownLink({
	source,
	sourceNum,
	questionNum,
	className,
}: {
	source: string;
	sourceNum: number;
	questionNum: number;
	className?: string;
}) {
	const isMobileLayout = useMobileLayout();
	return (
		<Stack direction={isMobileLayout ? "column" : "row"} spacing={2} width={"100%"}>
			<Typography className={styles.sourceNumber}>
				{i18n.t("portal.components.contentProtectionPageTSX.review.compromisedQuestions.sourceNumber", {
					number: sourceNum,
				})}
			</Typography>
			<Link
				className={`${styles.sourceLink} ${className ? className : ""}`}
				href={source}
				target="_blank"
				rel="noopener noreferrer"
				aria-label={i18n.t("portal.components.contentProtectionPageTSX.review.accessibility.linkAriaLabel", {
					sourceNum: sourceNum,
					questionNum: questionNum,
				})}
				onClick={(e) => e.stopPropagation()}
			>
				<Typography component={"span"} className={styles.sourceText}>
					{source}
				</Typography>
			</Link>
		</Stack>
	);
}

export function TakedownQuestion({ question }: { question: string }) {
	return (
		<Typography variant="h4" className={styles.question}>
			{question}
		</Typography>
	);
}

export function QuestionNumber({ questionNumber }: { questionNumber: number }) {
	return (
		<div className={styles.questionNumberContainer}>
			<Typography>
				{i18n.t("portal.components.contentProtectionPageTSX.review.qNumber", { number: questionNumber })}
			</Typography>
		</div>
	);
}

export function TakedownNoResults() {
	return (
		<Stack spacing={1.5} alignItems={"center"}>
			<TakedownNoResultsImage />
			<Stack spacing={1} alignItems={"center"}>
				<Typography variant="h3" color={"#333333"} fontWeight={500} fontSize={"14px"}>
					{i18n.t("portal.components.contentProtectionPageTSX.review.emptyTitle")}
				</Typography>
				<Typography fontWeight={400} fontSize={14}>
					{i18n.t("portal.components.contentProtectionPageTSX.review.emptyDescription")}
				</Typography>
			</Stack>
		</Stack>
	);
}

export function ClickableQuestion({
	question,
	questionQuery,
	questionNumber,
	tabpanel,
	children,
	isMobileLayout,
}: {
	question: DMCAQuestion;
	questionNumber: number;
	questionQuery: string;
	tabpanel: "freq" | "compromised";
	children: React.ReactNode;
	isMobileLayout: boolean;
}) {
	const [sourcesExpanded, setSourcesExpanded] = useState<boolean>(false);

	const expandButtonAriaLabelKey = sourcesExpanded ? "collapse" : "expand";
	const expandButtonAriaLabel = i18n.t(
		`portal.components.contentProtectionPageTSX.review.accessibility.${expandButtonAriaLabelKey}`,
		{ number: questionNumber, question: questionQuery },
	);

	const foundInXKey =
		tabpanel === "freq"
			? "portal.components.contentProtectionPageTSX.review.freqUsedQuestions.foundInXWebsites"
			: "portal.components.contentProtectionPageTSX.review.compromisedQuestions.sourcesFound";

	return (
		<Box className={styles.questionContainer}>
			<Button
				className={styles.questionHeaderWrapper}
				onClick={() => setSourcesExpanded(!sourcesExpanded)}
				aria-label={expandButtonAriaLabel}
				aria-expanded={sourcesExpanded}
			>
				<Stack
					direction={isMobileLayout ? "column" : "row"}
					spacing={isMobileLayout ? 1.5 : 1}
					minWidth={0}
					alignItems={isMobileLayout ? "center" : "flex-start"}
					width={"100%"}
				>
					<QuestionNumber questionNumber={questionNumber} />
					<TakedownQuestion question={questionQuery} />
				</Stack>
				<Stack spacing={isMobileLayout ? 0.2 : 1} direction="row" alignItems={"baseline"}>
					<Typography className={styles.foundInText}>
						<Trans
							i18nKey={foundInXKey}
							components={{ b: <strong /> }}
							values={{ number: question.numSources.toLocaleString() }}
						/>
					</Typography>
					<CoreExpandButton
						className={styles.dmcaExpandButton}
						variant="plain"
						expanded={sourcesExpanded}
						onChange={() => setSourcesExpanded(!sourcesExpanded)}
						aria-label={expandButtonAriaLabel}
					/>
				</Stack>
			</Button>
			{sourcesExpanded && <Box className={styles.expandedContent}>{children}</Box>}
		</Box>
	);
}
